import React, { forwardRef, useCallback } from 'react';
import match from 'mime-match';

enum FILE_TYPE_ERROR {
  FileTypeNotAllowed = 'FILE_TYPE_NOT_ALLOWED',
}

const checkFilesForValidType = (accept?: string, files?: FileList | null) => {
  let allFilesAreValid;

  if (files && files.length) {
    const filesArray = Array.from(files);
    allFilesAreValid = filesArray.every((file) => checkForValidFileType(accept, file));
  }

  return allFilesAreValid;
};

const checkForValidFileType = (accept?: string, file?: File) => {
  /**
   * if accept prop and file exists
   */
  if (accept && file) {
    const fileExtension = file.name.split('.').pop();

    const allowedFileTypes = accept.split(',').map((t) => t.trim());

    const isCorrectFileType = allowedFileTypes.some((type) => {
      /**
       * check if this is a mime-type
       */
      if (type.includes('/')) {
        if (!file.type) return false;
        return match(file.type.replace(/;.*?$/, ''), type);
      }

      /**
       * otherwise this is likely an extension
       */
      if (type[0] === '.' && fileExtension) {
        return fileExtension.toLowerCase() === type.slice(1).toLowerCase();
      }
      return false;
    });

    return isCorrectFileType;
  }

  return true;
};

const FileInput = forwardRef<
  HTMLInputElement,
  Omit<React.HTMLProps<HTMLInputElement>, 'type'> & {
    onFileSelectError?: (error?: typeof FILE_TYPE_ERROR) => void;
  }
>((props, ref) => {
  const { accept, onChange, onDrop, onFileSelectError, ...restProps } = props;

  const onFileDropHandle = useCallback(
    (e: React.DragEvent<HTMLInputElement>) => {
      const files = e?.dataTransfer?.files;

      const allFilesValid = checkFilesForValidType(accept, files);

      if (!allFilesValid) {
        e.preventDefault();
        onFileSelectError?.(FILE_TYPE_ERROR);
      } else {
        onDrop?.(e);
      }
    },
    [accept, onDrop, onFileSelectError],
  );

  const onFileChangeHandle = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e?.target?.files;

      const allFilesValid = checkFilesForValidType(accept, files);

      if (!allFilesValid) {
        e.preventDefault();
        onFileSelectError?.(FILE_TYPE_ERROR);
      } else {
        onChange?.(e);
      }
    },
    [accept, onChange, onFileSelectError],
  );

  return (
    <input
      ref={ref}
      accept={accept}
      type="file"
      onChange={onFileChangeHandle}
      onDrop={onFileDropHandle}
      {...restProps}
    />
  );
});

export { FILE_TYPE_ERROR };
export default React.memo(FileInput);
